// ─── Component Registry ────────────────────────────────────────────────────────
//
// Layer 4 instance-registration authority. Stores SchemaComponent instances
// paired with their resolved ComponentCapabilityContracts. Enforces all five
// R-series Registration Invariants at component registration time.
//
// Registration Invariants:
//   R-1  Unknown Component Type    - hard error
//   R-2  Duplicate Component ID    - hard error
//   R-3  Category Mismatch         - warning
//   R-4  Required Props Missing    - warning
//   R-5  Children on Non-Container - warning
//
// This file:
//   - stores authoritative inputs only: {component, contract}
//   - does not perform hydration
//   - does not perform binding validation
//   - does not perform rendering
//   - does not perform page mode detection
//   - consumes contract authority from componentContracts.ts
//
// Import path: @/components/componentRegistry

import { requireContract } from '@/components/componentContracts';
import { logger } from '@/system/logger';
import type { SchemaComponent } from '@/core/schema/appSchema';
import type { ComponentCapabilityContract } from '@/components/componentContracts';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RegisteredComponent {
  component: SchemaComponent;
  contract: ComponentCapabilityContract;
}

export interface RegistrationResult {
  componentId: string;
  warnings: string[];
}

// ─── Error Classes ────────────────────────────────────────────────────────────

export class ComponentRegistrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ComponentRegistrationError';
  }
}

export class ComponentNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ComponentNotFoundError';
  }
}

// ─── Module State ─────────────────────────────────────────────────────────────

const _registry = new Map<string, RegisteredComponent>();

// ─── Internal Helpers ─────────────────────────────────────────────────────────

// The only values of SchemaComponent.category that map to page-mode concepts.
// Used by R-3 to avoid false-positive warnings for functional category strings
// such as 'data', 'input', or 'layout'.
const PAGE_MODE_VALUES = new Set<string>(['marketing', 'app']);

// ─── Registration ─────────────────────────────────────────────────────────────

/**
 * Validates and registers a SchemaComponent instance against its capability contract.
 *
 * R-1 and R-2 are hard errors - registration throws on violation.
 * R-3, R-4, and R-5 are warnings - registration succeeds and warnings appear in result.
 *
 * Requires initializeContractRegistry() to have been called before the first
 * invocation. AppRuntimePhase3.boot() calls initializeContractRegistry() as its
 * first action, guaranteeing this precondition for Phase 6b.
 */
export function registerComponent(component: SchemaComponent): RegistrationResult {
  // R-1: Unknown Component Type - hard error.
  // No capability authority exists for this type; no downstream system can
  // operate on it safely. requireContract() throws ContractNotFoundError;
  // we wrap it as ComponentRegistrationError for consistent R-series reporting.
  let contract: ComponentCapabilityContract;
  try {
    contract = requireContract(component.type);
  } catch {
    throw new ComponentRegistrationError(
      `R-1: Component "${component.id}" has unknown type "${component.type}". ` +
      `No capability contract is registered for this type.`
    );
  }

  // R-2: Duplicate Component ID - hard error.
  // All downstream systems (hydration, binding resolution, tree traversal) address
  // components by ID. A duplicate makes the registry's ID-based lookups ambiguous.
  if (_registry.has(component.id)) {
    throw new ComponentRegistrationError(
      `R-2: Component ID "${component.id}" is already registered. Component IDs must be unique.`
    );
  }

  const warnings: string[] = [];

  // R-3: Category Mismatch - warning.
  // Only evaluated when component.category carries a recognized page-mode value
  // ('marketing' or 'app'). Non-page-mode strings (e.g. 'data', 'input') are not
  // checked to avoid false positives.
  if (
    PAGE_MODE_VALUES.has(component.category) &&
    !contract.pageCompatibility.includes(component.category as 'marketing' | 'app')
  ) {
    warnings.push(
      `R-3: Component "${component.id}" (type: "${component.type}") ` +
      `has category "${component.category}" but contract pageCompatibility is ` +
      `[${contract.pageCompatibility.join(', ')}].`
    );
  }

  // R-4: Required Props Missing - warning.
  // Binding resolution (Step 3) may satisfy required props at runtime via
  // BindingDefinitions absent from SchemaComponent.props at registration time.
  const missingProps = contract.structural.requiredProps.filter(
    prop => !(prop in component.props)
  );
  if (missingProps.length > 0) {
    warnings.push(
      `R-4: Component "${component.id}" (type: "${component.type}") ` +
      `is missing required props: [${missingProps.join(', ')}]. ` +
      `These may be satisfied by bindings at runtime.`
    );
  }

  // R-5: Children on Non-Container - warning.
  // Rendering policy for this case is owned by the hydration and rendering layers,
  // not the registry.
  if (
    Array.isArray(component.children) &&
    component.children.length > 0 &&
    !contract.structural.canContainChildren
  ) {
    warnings.push(
      `R-5: Component "${component.id}" (type: "${component.type}") ` +
      `has ${component.children.length} child ID(s) but contract declares ` +
      `canContainChildren: false. Rendering behavior is deferred to the hydration layer.`
    );
  }

  _registry.set(component.id, { component, contract });

  if (warnings.length > 0) {
    logger.warn(
      `ComponentRegistry: registered "${component.id}" with ${warnings.length} warning(s)`,
      { warnings },
    );
  } else {
    logger.debug(`ComponentRegistry: registered "${component.id}" (type: "${component.type}")`);
  }

  return { componentId: component.id, warnings };
}

// ─── Lookup ───────────────────────────────────────────────────────────────────

/**
 * Returns the RegisteredComponent for the given ID.
 * Throws ComponentNotFoundError if not registered.
 */
export function requireComponent(id: string): RegisteredComponent {
  const entry = _registry.get(id);
  if (!entry) {
    throw new ComponentNotFoundError(
      `Component "${id}" is not registered. Ensure registerComponent() was called for this ID.`
    );
  }
  return entry;
}

/** Returns the RegisteredComponent for the given ID, or undefined if not registered. */
export function getComponent(id: string): RegisteredComponent | undefined {
  return _registry.get(id);
}

/** Whether a component with the given ID is registered. */
export function hasComponent(id: string): boolean {
  return _registry.has(id);
}

/** All currently registered components. */
export function getAllComponents(): RegisteredComponent[] {
  return Array.from(_registry.values());
}

/** All registered components whose type matches the given type string. */
export function getComponentsOfType(type: string): RegisteredComponent[] {
  return Array.from(_registry.values()).filter(e => e.component.type === type);
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/**
 * Clears all registered component instances.
 * Called by AppRuntimePhase3._cleanup() on shutdown and error.
 *
 * Component contracts (Platform Facts, owned by componentContracts.ts) are NOT
 * cleared here. Use clearContracts() from componentContracts.ts for that.
 */
export function clearComponentRegistry(): void {
  _registry.clear();
  logger.debug('ComponentRegistry: cleared');
}
